import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { BookingForm } from '../components/BookingForm';
import { Phone, MessageSquare, CheckCircle, Clock, MapPin, Shield, Zap } from 'lucide-react';
import { businessInfo, services } from '../mock';

export const Emergency = () => {
  const service = services.find(s => s.id === 'emergency');
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-900/90 to-blue-950/80"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600/20 border border-red-500/30 rounded-full backdrop-blur-sm mb-6 animate-pulse">
              <Zap className="w-4 h-4 text-red-400" />
              <span className="text-red-400 text-sm font-semibold">Emergency Service Available Now</span>
            </div>

            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6">
              24/7 Emergency Callout
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Vehicle emergency? We're here to help, anytime, anywhere. Our mobile team is ready to assist you 24 hours a day, 7 days a week.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href={`tel:${businessInfo.phone}`}
                className="flex items-center justify-center space-x-2 px-10 py-5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg shadow-red-500/50 text-lg"
              >
                <Phone className="w-6 h-6" />
                <span>Emergency: {businessInfo.phone}</span>
              </a>
              <a
                href={`sms:${businessInfo.phone}`}
                className="flex items-center justify-center space-x-2 px-8 py-5 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all border border-slate-700"
              >
                <MessageSquare className="w-5 h-5" />
                <span>Send SMS</span>
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-slate-900/50 backdrop-blur-sm rounded-lg border border-slate-800">
                <Clock className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-white font-semibold">24/7/365</p>
                <p className="text-gray-400 text-sm">Always Available</p>
              </div>
              <div className="p-4 bg-slate-900/50 backdrop-blur-sm rounded-lg border border-slate-800">
                <MapPin className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-white font-semibold">Mobile Service</p>
                <p className="text-gray-400 text-sm">We Come to You</p>
              </div>
              <div className="p-4 bg-slate-900/50 backdrop-blur-sm rounded-lg border border-slate-800">
                <Zap className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-white font-semibold">Rapid Response</p>
                <p className="text-gray-400 text-sm">Fast Arrival Times</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Covered */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Emergency Services We Provide
            </h2>
            <p className="text-xl text-gray-400">
              Full range of emergency automotive services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Vehicle Lockouts",
                description: "Locked out of your car? We'll get you back in safely and quickly",
                icon: "🔐"
              },
              {
                title: "Lost Keys",
                description: "Emergency key cutting and programming on-site",
                icon: "🔑"
              },
              {
                title: "Broken Keys",
                description: "Key extraction and replacement service",
                icon: "🔧"
              },
              {
                title: "Ignition Problems",
                description: "Ignition repair and replacement",
                icon: "⚡"
              },
              {
                title: "Immobilizer Issues",
                description: "Emergency programming and diagnostics",
                icon: "🚗"
              },
              {
                title: "Lock Repairs",
                description: "Vehicle lock mechanism repairs",
                icon: "🛠️"
              }
            ].map((serviceItem, idx) => (
              <div key={idx} className="p-6 bg-slate-800 rounded-xl border border-slate-700 hover:border-blue-600 transition-all">
                <div className="text-4xl mb-3">{serviceItem.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">{serviceItem.title}</h3>
                <p className="text-gray-400 text-sm">{serviceItem.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute inset-0 bg-blue-600 rounded-2xl blur-3xl opacity-20"></div>
              <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl border border-slate-800">
                <h3 className="text-2xl font-bold text-white mb-6">Why Call Us in an Emergency?</h3>
                <div className="space-y-4">
                  {[
                    "Available 24 hours a day, every day of the year",
                    "Experienced, professional technicians",
                    "Fully equipped mobile service vehicles",
                    "Transparent, upfront pricing",
                    "No call-out charges",
                    "Fully insured and certified",
                    "Covering all of Cambridgeshire",
                    "Fast response times - typically 30-45 minutes"
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-white mb-6">
                Round-the-Clock Support
              </h2>
              <p className="text-xl text-gray-400 mb-6">
                Vehicle emergencies don't wait for business hours, and neither do we. Our team is on standby 24/7 to help you when you need it most.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="p-4 bg-slate-900 rounded-lg border-l-4 border-blue-600">
                  <h4 className="font-bold text-white mb-1">Night & Weekend Service</h4>
                  <p className="text-gray-400 text-sm">No extra charges for evenings, nights, or weekends</p>
                </div>
                <div className="p-4 bg-slate-900 rounded-lg border-l-4 border-blue-600">
                  <h4 className="font-bold text-white mb-1">Bank Holidays</h4>
                  <p className="text-gray-400 text-sm">We work when you need us, including all public holidays</p>
                </div>
                <div className="p-4 bg-slate-900 rounded-lg border-l-4 border-blue-600">
                  <h4 className="font-bold text-white mb-1">Mobile Service</h4>
                  <p className="text-gray-400 text-sm">We come to your location - home, work, or roadside</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={`tel:${businessInfo.phone}`}
                  className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all hover:scale-105"
                >
                  <Phone className="w-5 h-5" />
                  <span>Call Now</span>
                </a>
                <button
                  onClick={() => setIsBookingOpen(true)}
                  className="flex items-center justify-center space-x-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all border border-slate-700"
                >
                  <span>Schedule Callout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Emergency Coverage Area
            </h2>
            <p className="text-xl text-gray-400">
              Fast response across Cambridgeshire and surrounding areas
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {['Ely', 'Cambridge', 'King\'s Lynn', 'Peterborough', 'Bury St Edmunds', 'Norwich', 'March', 'Newmarket', 'Huntingdon', 'St Neots', 'Wisbech', 'Downham Market'].map((area) => (
              <div key={area} className="p-3 bg-slate-800 rounded-lg text-center border border-slate-700">
                <MapPin className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                <p className="text-white text-sm font-medium">{area}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-gray-400 mb-4">Not in the list? Contact us anyway - we may be able to help!</p>
            <a
              href={`tel:${businessInfo.phone}`}
              className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 font-semibold"
            >
              <Phone className="w-4 h-4" />
              <span>{businessInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-950 to-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600/20 border border-red-500/30 rounded-full backdrop-blur-sm mb-6 animate-pulse">
            <Zap className="w-4 h-4 text-red-400" />
            <span className="text-red-400 text-sm font-semibold">Emergency Service Active</span>
          </div>
          
          <h2 className="text-4xl font-bold text-white mb-6">
            Need Emergency Assistance Right Now?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Don't wait - call us immediately for fast, professional help
          </p>
          <a
            href={`tel:${businessInfo.phone}`}
            className="inline-flex items-center justify-center space-x-2 px-12 py-5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg shadow-red-500/50 text-xl"
          >
            <Phone className="w-6 h-6" />
            <span>Emergency Call: {businessInfo.phone}</span>
          </a>
          <p className="text-gray-400 text-sm mt-6">
            Available 24 hours a day, 7 days a week, 365 days a year
          </p>
        </div>
      </section>

      <Footer />

      {/* Booking Modal */}
      <BookingForm
        service={service}
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
      />
    </div>
  );
};

export default Emergency;
